<template>
	<div v-if="isEnteringUserInformation" class="fullscreen-overlay">
		<close-btn @click="cancelEnterUserInformation"/>
		<h2 class="overlay-header">Enter User Information</h2>

		<div class="overlay-body">
			<div class="user-info-container">
				<div>
					<h6 :class="{'error': invalidFirstName}">First Name</h6>
					<input
						id="firstName"
						type="text"
						:class="{'error': invalidFirstName}"
						placeholder="First Name"
						v-model="firstName"
						@input="validateFirstName"
					/>
					<p :class="{'error': invalidFirstName}">Please enter a first name</p>
				</div>

				<div>
					<h6 :class="{'error': invalidLastName}">Last Name</h6>
					<input
						id="lastName"
						type="text"
						:class="{'error': invalidLastName}"
						placeholder="Last Name"
						v-model="lastName"
						@input="validateLastName"
					/>
					<p :class="{'error': invalidLastName}">Please enter a last name</p>
				</div>

				<div>
					<h6 :class="{'error': invalidEmail}">Email</h6>
					<input
						id="email"
						type="email"
						autocomplete="off"
						:class="{'error': invalidEmail}"
						placeholder="Email"
						v-model="email"
						@input="validateEmail"
					/>
					<p :class="{'error': invalidEmail}">Please enter an email</p>
				</div>

				<div>
					<h6 :class="{'error': invalidConfirmEmail}">Confirm Email</h6>
					<input
						id="confirmEmail"
						type="email"
						autocomplete="off"
						:class="{'error': invalidConfirmEmail}"
						placeholder="Confirm Email"
						v-model="confirmEmail"
						@input="validateConfirmEmail"
					/>
					<p :class="{'error': invalidConfirmEmail}">The emails must match</p>
				</div>

				<div>
					<h6 :class="{'error': invalidPhone}">Phone</h6>
					<input
						id="phone"
						type="tel"
						:class="{'error': invalidPhone}"
						placeholder="Phone No."
						v-model="phone"
						@input="validatePhone"
					/>
					<p :class="{'error': invalidPhone}">Please enter a phone number</p>
				</div>

				<div>
					<h6>Ticket</h6>
					<select v-if="hasAvailableTickets" name="tickets" id="tickets" v-model="selectedTicket">
						<template v-for="ticket in $scanner.availableTickets">
							<option
								:value="ticket.id"
								:disabled="ticket.is_sold_out"
								:selected="ticket.id === selectedTicket"
								:key="ticket.id"
							>{{ ticket.title }}{{ ticket.is_sold_out ? ' (Sold Out)' : '' }}</option>
						</template>
					</select>
					<p v-else>No Tickets Available</p>
				</div>
			</div>

			<div class="p-1">
				<button @click="done"
						:disabled="!isFormValid || isCheckingAvailability"
						class="better-mobile-btn">
					{{ isCheckingAvailability ? 'Checking...' : 'Next' }}
				</button>
			</div>
		</div>

		<ErrorPopup
			:show="shouldShowError"
			:error-message="errorMessage"
			btn-text="Ok"
			@close="closeError"
		/>
	</div>
</template>

<script>
import CloseBtn from "@/components/CloseBtn.vue";
import ErrorPopup from "@/components/ErrorPopup.vue";
import {isEmailFormat, isNonEmpty, isPhoneFormat} from "../../public/fieldValidators.js";

export default {
	components: {
		CloseBtn,
		ErrorPopup,
	},
	props: {
		isEnteringUserInformation: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			firstName: "",
			lastName: "",
			email: "",
			confirmEmail: "",
			phone: "",
			selectedTicket: null,
			isCheckingAvailability: false,
			shouldShowError: false,
			errorMessage: "Oops!",
			invalidFirstName: false,
			invalidLastName: false,
			invalidEmail: false,
			invalidConfirmEmail: false,
			invalidPhone: false,
		}
	},
	computed: {
		isFormValid() {
			return this.firstName !== "" &&
				this.lastName !== "" &&
				this.email !== "" &&
				this.email === this.confirmEmail &&
				this.phone !== "" &&
				this.selectedTicket !== null &&
				!this.isSelectedTicketSoldOut
		},
		hasAvailableTickets() {
			return this.$scanner.availableTickets && this.$scanner.availableTickets.length > 0
		},
		isSelectedTicketSoldOut() {
			const ticket = this.hasAvailableTickets
				? this.$scanner.availableTickets.find(t => t.id === this.selectedTicket)
				: null
			return !!ticket && !!ticket.is_sold_out
		},
		firstAvailableTicketId() {
			if (!this.hasAvailableTickets) {
				return null
			}
			const firstInStock = this.$scanner.availableTickets.find(t => !t.is_sold_out)
			return firstInStock ? firstInStock.id : null
		}
	},
	watch: {
		isEnteringUserInformation(newValue) {
			if (newValue) {
				this.refreshAvailableTickets()
			}
		}
	},
	methods: {
		/* NOTE: $scanner is a plain object (window.scanner), never made Vue-reactive - mutating
		 * $scanner.availableTickets doesn't cause this component to re-render on its own, so
		 * every refresh here must explicitly $forceUpdate() once the new data has landed. (The
		 * app's own $scanner.rerender() looks like it exists for this exact purpose, but
		 * Scanner.vue's ReRender() sets `this.renderCount`, not the `rerenderCount` the root
		 * element's :key is actually bound to, so it's a no-op - left alone, not fixed here.) */
		refreshAvailableTickets() {
			return this.$scanner.getAvailableTickets().then(() => {
				if (!this.selectedTicket) {
					this.selectedTicket = this.firstAvailableTicketId
				}
				this.$forceUpdate()
			}).catch(() => {})
		},
		async done() {
			if (!this.isFormValid || this.isCheckingAvailability) {
				return
			}

			/* NOTE: Live re-check before advancing into terms-signing - tickets can sell out via
			 * another scanner OR the ticket portal between this modal opening and Next being
			 * pressed, and $scanner.availableTickets is otherwise only refreshed on app load. */
			this.isCheckingAvailability = true
			try {
				await this.$scanner.getAvailableTickets()
			} catch (err) {
				this.isCheckingAvailability = false
				this.$forceUpdate()
				this.showError("Unable to confirm ticket availability. Please try again.")
				return
			}
			this.isCheckingAvailability = false
			this.$forceUpdate()

			if (this.isSelectedTicketSoldOut) {
				this.showError("That ticket just sold out. Please select a different ticket.")
				return
			}

			this.$emit('submit', {
				firstName: this.firstName,
				lastName: this.lastName,
				email: this.email,
				phone: this.phone,
				selectedTicket: this.selectedTicket,
			})
		},
		showError(msg) {
			this.shouldShowError = true
			this.errorMessage = msg
		},
		closeError() {
			this.shouldShowError = false
		},
		resetState() {
			this.firstName = ""
			this.lastName = ""
			this.email = ""
			this.confirmEmail = ""
			this.phone = ""
			this.selectedTicket = null
		},
		cancelEnterUserInformation() {
			this.resetState()
			this.$emit('cancel')
		},
		validateFirstName() {
			this.invalidFirstName = !isNonEmpty(this.firstName)
		},
		validateLastName() {
			this.invalidLastName = !isNonEmpty(this.lastName)
		},
		validateEmail() {
			this.invalidEmail = !isEmailFormat(this.email)
			this.validateConfirmEmail()
		},
		validateConfirmEmail() {
			this.invalidConfirmEmail = !isEmailFormat(this.confirmEmail) || this.email !== this.confirmEmail;
		},
		validatePhone() {
			this.invalidPhone = !isPhoneFormat(this.phone)
		},
	},
}
</script>

<style scoped>
.user-info-container {
	text-align: left;
	padding: 1em;

	display: flex;
	flex-direction: column;
	gap: 1.5em;
	margin: 0 auto;

	width: 100%;
	max-width: 40em;
}

.user-info-container input, .user-info-container select {
	margin: 0;
	width: 100%;
}

.user-info-container p {
	margin: 0 0 0 0;
	font-style: italic;
	display: none;
}

.user-info-container p.error {
	display: block;
}
</style>